import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { 
    FiCheckSquare, 
    FiRefreshCw,
    FiChevronDown,
    FiChevronUp
} from 'react-icons/fi';
import UserProfileButton from './common/UserProfileButton';
import LogoutButton from './common/LogoutButton';
import receptionBg from '../assets/images/backgrounds/reception-bg.png';
import './ManagerDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Company full name -> short form for table display
const COMPANY_SHORT_FORM = {
    'BRADMA QATAR FOOD (BQF)': 'BQF',
    'AHLA MART TRADING': 'AMT',
    'BRADMA QATAR WLL': 'BMMW',
    'CENTRAL AUTO PARTS (CAP)': 'CAP'
};

const getCompanyShortForm = (company) => {
    if (!company) return 'N/A';
    return COMPANY_SHORT_FORM[company] ?? company;
};

// Feature Flags - Set to false to disable features
const FEATURES = {
    TASKS_ENABLED: true  // Set to true to enable Tasks feature
};

const ManagerDashboard = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [showModulesDropdown, setShowModulesDropdown] = useState(false);
    const [hasTaskNotifications, setHasTaskNotifications] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Dashboard Overview State
    const [dashboardStats, setDashboardStats] = useState({
        cheques: { submitted: { count: 0, amount: 0 }, collected: { count: 0, amount: 0 }, pending: { count: 0, amount: 0 } },
        cashPayments: { submitted: { count: 0, amount: 0 }, collected: { count: 0, amount: 0 }, pending: { count: 0, amount: 0 } },
        registerBook: { checkIns: 0, checkOuts: 0, currentlyCheckedIn: 0 },
        documentLogBook: { created: 0, completed: 0, pending: 0 }
    });
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [dashboardFromDate, setDashboardFromDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return date.toISOString().split('T')[0];
    });
    const [dashboardToDate, setDashboardToDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });

    // Cheque Register State
    const [allCheques, setAllCheques] = useState([]);
    const [loadingCheques, setLoadingCheques] = useState(false);
    const [searchCheques, setSearchCheques] = useState('');
    const [chequeStatusFilter, setChequeStatusFilter] = useState('');
    const [chequeCompanyFilter, setChequeCompanyFilter] = useState('');
    const [chequeSupplierFilter, setChequeSupplierFilter] = useState('');
    const [chequeSuppliersList, setChequeSuppliersList] = useState([]);
    const [chequeFromDate, setChequeFromDate] = useState('');
    const [chequeToDate, setChequeToDate] = useState('');
    const [currentChequePage, setCurrentChequePage] = useState(1);
    const [totalChequePages, setTotalChequePages] = useState(1);
    const [viewChequeModal, setViewChequeModal] = useState(null);
    const [chequeViewMode, setChequeViewMode] = useState('list');
    const [chequeGroupByField, setChequeGroupByField] = useState('company');
    const [chequeGroupedData, setChequeGroupedData] = useState([]);
    const [chequeExpandedGroups, setChequeExpandedGroups] = useState(new Set());
    const [chequeGroupDetails, setChequeGroupDetails] = useState({});
    const [chequeGroupPages, setChequeGroupPages] = useState({});
    const [loadingChequeGroups, setLoadingChequeGroups] = useState(new Set());

    // Cashbook State
    const [allCashbook, setAllCashbook] = useState([]);
    const [loadingCashbook, setLoadingCashbook] = useState(false);
    const [searchCashbook, setSearchCashbook] = useState('');
    const [cashbookStatusFilter, setCashbookStatusFilter] = useState('');
    const [cashbookCompanyFilter, setCashbookCompanyFilter] = useState('');
    const [cashbookSupplierFilter, setCashbookSupplierFilter] = useState('');
    const [cashbookSuppliersList, setCashbookSuppliersList] = useState([]);
    const [cashbookFromDate, setCashbookFromDate] = useState('');
    const [cashbookToDate, setCashbookToDate] = useState('');
    const [currentCashbookPage, setCurrentCashbookPage] = useState(1);
    const [totalCashbookPages, setTotalCashbookPages] = useState(1);
    const [cashbookViewMode, setCashbookViewMode] = useState('list');
    const [cashbookGroupByField, setCashbookGroupByField] = useState('company');
    const [cashbookGroupedData, setCashbookGroupedData] = useState([]);
    const [cashbookExpandedGroups, setCashbookExpandedGroups] = useState(new Set());
    const [cashbookGroupDetails, setCashbookGroupDetails] = useState({});
    const [cashbookGroupPages, setCashbookGroupPages] = useState({});
    const [loadingCashbookGroups, setLoadingCashbookGroups] = useState(new Set());

    // Document Log Book State
    const [completedDocs, setCompletedDocs] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [searchDocs, setSearchDocs] = useState('');
    const [docsStatusFilter, setDocsStatusFilter] = useState('');
    const [docsFromDate, setDocsFromDate] = useState('');
    const [docsToDate, setDocsToDate] = useState('');
    const [currentDocsPage, setCurrentDocsPage] = useState(1);
    const [totalDocsPages, setTotalDocsPages] = useState(1);

    // Register Book State
    const [allRegister, setAllRegister] = useState([]);
    const [loadingRegister, setLoadingRegister] = useState(false);
    const [searchRegister, setSearchRegister] = useState('');
    const [registerStatusFilter, setRegisterStatusFilter] = useState('');
    const [registerFromDate, setRegisterFromDate] = useState('');
    const [registerToDate, setRegisterToDate] = useState('');
    const [currentRegisterPage, setCurrentRegisterPage] = useState(1);
    const [totalRegisterPages, setTotalRegisterPages] = useState(1);

    // Daily Documents State
    const [monthlyDoc, setMonthlyDoc] = useState(null);
    const [loadingMonthly, setLoadingMonthly] = useState(false);
    const [businessGroups, setBusinessGroups] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedBusinessGroup, setSelectedBusinessGroup] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Daily Report Status State
    const [dailyReportStatus, setDailyReportStatus] = useState(null);
    const [loadingDailyReport, setLoadingDailyReport] = useState(false);
    const [selectedReportDate, setSelectedReportDate] = useState(() => {
        // Default to yesterday
        const date = new Date();
        date.setDate(date.getDate() - 1);
        return date.toISOString().split('T')[0];
    });
    
    // Calendar State
    const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
    const [monthlyStatuses, setMonthlyStatuses] = useState({});
    const [loadingCalendar, setLoadingCalendar] = useState(false);
    
    // Modal State for Daily Report Details
    const [showReportModal, setShowReportModal] = useState(false);

    const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

    // Fetch task notification count
    const fetchTaskNotifications = async () => {
        try {
            const response = await axiosInstance.get('/api/tasks/notification-count');
            if (response.data.success) {
                setHasTaskNotifications(response.data.data.hasNotifications);
            }
        } catch (err) {
            console.error('Error fetching task notifications:', err);
        }
    };

    // Fetch Dashboard Statistics
    const fetchDashboardStats = async (fromDate, toDate) => {
        setLoadingDashboard(true);
        try {
            // Use new aggregated dashboard API
            const response = await axiosInstance.get(
                `/api/dashboard/stats?fromDate=${fromDate}&toDate=${toDate}`
            );

            const data = response.data.data;
            
            setDashboardStats({
                cheques: data.cheques,
                cashPayments: data.cashbook,
                registerBook: data.registerBook,
                documentLogBook: data.documentLogBook,
                tasks: data.tasks
            });
        } catch (err) {
            console.error('Error fetching dashboard stats:', err);
            // Set empty stats on error
            setDashboardStats({
                cheques: { total: 0, pending: 0, approved: 0, rejected: 0, totalAmount: 0 },
                cashPayments: { total: 0, pending: 0, approved: 0, rejected: 0, totalReceipts: 0, totalPayments: 0, balance: 0 },
                registerBook: { total: 0, checkedIn: 0, checkedOut: 0 },
                documentLogBook: { total: 0, received: 0, dispatched: 0, pending: 0 },
                tasks: { total: 0, todo: 0, inProgress: 0, review: 0, completed: 0, pendingApproval: 0, overdue: 0 }
            });
        } finally {
            setLoadingDashboard(false);
        }
    };

    // Fetch Cheques
    const fetchCheques = async (page = 1, search = '', status = '', company = '', supplier = '', fromDate = '', toDate = '') => {
        setLoadingCheques(true);
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/cheques?page=${page}&limit=10&search=${search}&status=${status}&company=${company}&supplier=${supplier}&fromDate=${fromDate}&toDate=${toDate}`
            );
            if (response.data.success) {
                setAllCheques(response.data.data);
                setTotalChequePages(response.data.totalPages);
                setCurrentChequePage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching cheques:', err);
        } finally {
            setLoadingCheques(false);
        }
    };
    
    const fetchChequeSuppliersList = async () => {
        try {
            const response = await axiosInstance.get('/api/dashboard/cheques/suppliers');
            if (response.data.success) {
                setChequeSuppliersList(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching suppliers:', err);
        }
    };
    
    const fetchGroupedCheques = async (groupBy = chequeGroupByField) => {
        setLoadingCheques(true);
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/cheques/grouped?groupBy=${groupBy}&status=${chequeStatusFilter}&company=${chequeCompanyFilter}&supplier=${chequeSupplierFilter}&fromDate=${chequeFromDate}&toDate=${chequeToDate}`
            );
            if (response.data.success) {
                setChequeGroupedData(response.data.data);
                setChequeGroupDetails({});
                setChequeGroupPages({});
                setChequeExpandedGroups(new Set());
            }
        } catch (err) {
            console.error('Error fetching grouped cheques:', err);
        } finally {
            setLoadingCheques(false);
        }
    };
    
    const fetchChequeGroupDetails = async (groupId, page = 1) => {
        const loadingKey = `${groupId}-${page}`;
        setLoadingChequeGroups(prev => new Set(prev).add(loadingKey));
        
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/cheques/grouped/${chequeGroupByField}/${encodeURIComponent(groupId)}?page=${page}&limit=50&status=${chequeStatusFilter}&fromDate=${chequeFromDate}&toDate=${chequeToDate}`
            );
            
            if (response.data.success) {
                setChequeGroupDetails(prev => ({
                    ...prev,
                    [groupId]: {
                        cheques: response.data.data,
                        totalPages: response.data.totalPages,
                        currentPage: response.data.currentPage,
                        total: response.data.total
                    }
                }));
            }
        } catch (err) {
            console.error('Error fetching cheque group details:', err);
        } finally {
            setLoadingChequeGroups(prev => {
                const newSet = new Set(prev);
                newSet.delete(loadingKey);
                return newSet;
            });
        }
    };
    
    const toggleChequeGroup = (groupId) => {
        const newExpanded = new Set(chequeExpandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
            if (!chequeGroupDetails[groupId]) {
                fetchChequeGroupDetails(groupId, 1);
            }
        }
        setChequeExpandedGroups(newExpanded);
    };
    
    const handleChequeGroupPageChange = (groupId, newPage) => {
        setChequeGroupPages(prev => ({ ...prev, [groupId]: newPage }));
        fetchChequeGroupDetails(groupId, newPage);
    };

    // Fetch Cashbook
    const fetchCashbook = async (page = 1, search = '', status = '', company = '', supplier = '', fromDate = '', toDate = '') => {
        setLoadingCashbook(true);
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/cashbook?page=${page}&limit=10&search=${search}&status=${status}&company=${company}&supplier=${supplier}&fromDate=${fromDate}&toDate=${toDate}`
            );
            if (response.data.success) {
                setAllCashbook(response.data.data);
                setTotalCashbookPages(response.data.totalPages);
                setCurrentCashbookPage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching cashbook:', err);
        } finally {
            setLoadingCashbook(false);
        }
    };
    
    const fetchCashbookSuppliersList = async () => {
        try {
            const response = await axiosInstance.get('/api/dashboard/cashbook/suppliers');
            if (response.data.success) {
                setCashbookSuppliersList(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching cashbook suppliers:', err);
        }
    };
    
    const fetchGroupedCashbook = async (groupBy = cashbookGroupByField) => {
        setLoadingCashbook(true);
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/cashbook/grouped?groupBy=${groupBy}&status=${cashbookStatusFilter}&company=${cashbookCompanyFilter}&supplier=${cashbookSupplierFilter}&fromDate=${cashbookFromDate}&toDate=${cashbookToDate}`
            );
            if (response.data.success) {
                setCashbookGroupedData(response.data.data);
                setCashbookGroupDetails({});
                setCashbookGroupPages({});
                setCashbookExpandedGroups(new Set());
            }
        } catch (err) {
            console.error('Error fetching grouped cashbook:', err);
        } finally {
            setLoadingCashbook(false);
        }
    };
    
    const fetchCashbookGroupDetails = async (groupId, page = 1) => {
        const loadingKey = `${groupId}-${page}`;
        setLoadingCashbookGroups(prev => new Set(prev).add(loadingKey));
        
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/cashbook/grouped/${cashbookGroupByField}/${encodeURIComponent(groupId)}?page=${page}&limit=50&status=${cashbookStatusFilter}&fromDate=${cashbookFromDate}&toDate=${cashbookToDate}`
            );
            
            if (response.data.success) {
                setCashbookGroupDetails(prev => ({
                    ...prev,
                    [groupId]: {
                        cashbooks: response.data.data,
                        totalPages: response.data.totalPages,
                        currentPage: response.data.currentPage,
                        total: response.data.total
                    }
                }));
            }
        } catch (err) {
            console.error('Error fetching cashbook group details:', err);
        } finally {
            setLoadingCashbookGroups(prev => {
                const newSet = new Set(prev);
                newSet.delete(loadingKey);
                return newSet;
            });
        }
    };
    
    const toggleCashbookGroup = (groupId) => {
        const newExpanded = new Set(cashbookExpandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
            if (!cashbookGroupDetails[groupId]) {
                fetchCashbookGroupDetails(groupId, 1);
            }
        }
        setCashbookExpandedGroups(newExpanded);
    };
    
    const handleCashbookGroupPageChange = (groupId, newPage) => {
        setCashbookGroupPages(prev => ({ ...prev, [groupId]: newPage }));
        fetchCashbookGroupDetails(groupId, newPage);
    };

    // Fetch Document Log Book
    const fetchDocuments = async (page = 1, search = '', status = '', fromDate = '', toDate = '') => {
        setLoadingDocs(true);
        try {
            const url = `/api/dashboard/documents?page=${page}&limit=10&search=${search}&status=${status}&fromDate=${fromDate}&toDate=${toDate}`;
            
            const response = await axiosInstance.get(url);
            
            if (response.data.success) {
                setCompletedDocs(response.data.data);
                setTotalDocsPages(response.data.totalPages);
                setCurrentDocsPage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching documents:', err);
        } finally {
            setLoadingDocs(false);
        }
    };

    // Fetch Register Book
    const fetchRegister = async (page = 1, search = '', status = '', fromDate = '', toDate = '') => {
        setLoadingRegister(true);
        try {
            // Build query parameters
            let queryParams = `page=${page}&limit=10&search=${search}&fromDate=${fromDate}&toDate=${toDate}`;
            
            // Add status filter if specified
            if (status === 'CHECKED_IN' || status === 'CHECKED_OUT') {
                queryParams += `&status=${status}`;
            }
            
            const response = await axiosInstance.get(`/api/dashboard/register?${queryParams}`);
            if (response.data.success) {
                setAllRegister(response.data.data);
                setTotalRegisterPages(response.data.totalPages);
                setCurrentRegisterPage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching register:', err);
        } finally {
            setLoadingRegister(false);
        }
    };

    // Fetch Daily Documents Config
    const fetchBusinessGroups = async () => {
        try {
            const response = await axiosInstance.get('/api/dashboard/monthly-docs/config');
            if (response.data.success) {
                setBusinessGroups(response.data.businessGroups);
            }
        } catch (err) {
            console.error('Error fetching business groups:', err);
        }
    };

    const fetchBranches = async (businessGroup) => {
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/monthly-docs/config?businessGroup=${encodeURIComponent(businessGroup)}`
            );
            if (response.data.success) {
                setBranches(response.data.branches);
            }
        } catch (err) {
            console.error('Error fetching branches:', err);
        }
    };

    const fetchMonthlyDocument = async (businessGroup, branch, month, year) => {
        // Use parameters if provided, otherwise use state
        const bg = businessGroup || selectedBusinessGroup;
        const br = branch || selectedBranch;
        const m = month || selectedMonth;
        const y = year || selectedYear;
        
        if (!bg || !br) return;
        setLoadingMonthly(true);
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/monthly-docs/document?businessGroup=${encodeURIComponent(bg)}&branch=${encodeURIComponent(br)}&month=${m}&year=${y}`
            );
            if (response.data.success) {
                setMonthlyDoc(response.data.document);
            }
        } catch (err) {
            console.error('Error fetching daily document:', err);
        } finally {
            setLoadingMonthly(false);
        }
    };

    const fetchDailyReportStatus = async (date) => {
        setLoadingDailyReport(true);
        try {
            const response = await axiosInstance.get(`/api/dashboard/monthly-docs/daily-status?date=${date}`);
            if (response.data.success) {
                setDailyReportStatus(response.data);
            }
        } catch (err) {
            console.error('Error fetching daily report status:', err);
        } finally {
            setLoadingDailyReport(false);
        }
    };

    const fetchMonthlyStatuses = async (month, year) => {
        setLoadingCalendar(true);
        try {
            const response = await axiosInstance.get(
                `/api/dashboard/monthly-docs/month-status?month=${month + 1}&year=${year}`
            );
            if (response.data.success) {
                setMonthlyStatuses(response.data.dailyStatuses);
            }
        } catch (err) {
            console.error('Error fetching monthly statuses:', err);
        } finally {
            setLoadingCalendar(false);
        }
    };

    const handleDateSelect = (date) => {
        setSelectedReportDate(date);
        fetchDailyReportStatus(date);
        setShowReportModal(true); // Open modal when date is selected
        // Scroll to top when modal opens
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const handleMonthChange = (direction) => {
        let newMonth = calendarMonth + direction;
        let newYear = calendarYear;
        
        if (newMonth > 11) {
            newMonth = 0;
            newYear++;
        } else if (newMonth < 0) {
            newMonth = 11;
            newYear--;
        }
        
        setCalendarMonth(newMonth);
        setCalendarYear(newYear);
        fetchMonthlyStatuses(newMonth, newYear);
    };

    const renderCalendarPicker = () => {
        const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
        const firstDayOfMonth = new Date(calendarYear, calendarMonth, 1).getDay();
        const today = new Date().toISOString().split('T')[0];
        
        const days = [];
        
        // Add empty cells for days before the first day of month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-picker-day empty"></div>);
        }
        
        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const status = monthlyStatuses[day] || 'unknown';
            const isSelected = dateStr === selectedReportDate;
            const isToday = dateStr === today;
            const isFuture = new Date(dateStr) > new Date(today);
            
            days.push(
                <div
                    key={day}
                    className={`calendar-picker-day ${status} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''}`}
                    onClick={() => !isFuture && handleDateSelect(dateStr)}
                    title={isFuture ? 'Future date' : `${day} - ${status === 'complete' ? 'All reports received' : status === 'partial' ? 'Some reports pending' : status === 'none' ? 'No reports received' : 'No data'}`}
                >
                    <span className="day-number">{day}</span>
                    {!isFuture && status !== 'unknown' && (
                        <span className="day-status-icon">
                            {status === 'complete' && '✓'}
                            {status === 'partial' && '◐'}
                            {status === 'none' && '○'}
                        </span>
                    )}
                </div>
            );
        }
        
        return (
            <div className="calendar-picker-container">
                <div className="calendar-picker-header">
                    <button className="calendar-nav-btn" onClick={() => handleMonthChange(-1)}>
                        ‹
                    </button>
                    <h3 className="calendar-month-title">
                        {months[calendarMonth]} {calendarYear}
                    </h3>
                    <button className="calendar-nav-btn" onClick={() => handleMonthChange(1)}>
                        ›
                    </button>
                </div>
                <div className="calendar-picker-weekdays">
                    <div>Sun</div>
                    <div>Mon</div>
                    <div>Tue</div>
                    <div>Wed</div>
                    <div>Thu</div>
                    <div>Fri</div>
                    <div>Sat</div>
                </div>
                <div className="calendar-picker-grid">
                    {days}
                </div>
                <div className="calendar-picker-legend">
                    <div className="legend-item">
                        <span className="legend-icon complete">✓</span>
                        <span>Complete</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-icon partial">◐</span>
                        <span>Partial</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-icon none">○</span>
                        <span>Pending</span>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (activeTab === 'dashboard') fetchDashboardStats(dashboardFromDate, dashboardToDate);
        if (activeTab === 'cheques') {
            fetchChequeSuppliersList();
            if (chequeViewMode === 'list') {
                fetchCheques(currentChequePage, searchCheques, chequeStatusFilter, chequeCompanyFilter, chequeSupplierFilter, chequeFromDate, chequeToDate);
            } else {
                fetchGroupedCheques();
            }
        }
        if (activeTab === 'cashbook') {
            fetchCashbookSuppliersList();
            if (cashbookViewMode === 'list') {
                fetchCashbook(currentCashbookPage, searchCashbook, cashbookStatusFilter, cashbookCompanyFilter, cashbookSupplierFilter, cashbookFromDate, cashbookToDate);
            } else {
                fetchGroupedCashbook();
            }
        }
        if (activeTab === 'documents') fetchDocuments(currentDocsPage, searchDocs, docsStatusFilter, docsFromDate, docsToDate);
        if (activeTab === 'register') fetchRegister(currentRegisterPage, searchRegister, registerStatusFilter, registerFromDate, registerToDate);
        if (activeTab === 'monthly') {
            fetchBusinessGroups();
        }
        if (activeTab === 'dashboard') {
            fetchDailyReportStatus(selectedReportDate);
            fetchMonthlyStatuses(calendarMonth, calendarYear);
        }
        // Fetch task notifications on component mount and when returning to dashboard
        fetchTaskNotifications();
    }, [activeTab]);

    useEffect(() => {
        if (selectedBusinessGroup) {
            fetchBranches(selectedBusinessGroup);
        }
    }, [selectedBusinessGroup]);

    // Auto-fetch document when all required fields are selected
    useEffect(() => {
        if (selectedBusinessGroup && selectedBranch && selectedMonth && selectedYear) {
            fetchMonthlyDocument(selectedBusinessGroup, selectedBranch, selectedMonth, selectedYear);
        }
    }, [selectedBusinessGroup, selectedBranch, selectedMonth, selectedYear]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleTimeString('en-GB', {
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-QA', {
            style: 'currency', currency: 'QAR', minimumFractionDigits: 2
        }).format(amount);
    };

    const getDaysInMonth = (month, year) => {
        return new Date(year, month, 0).getDate();
    };

    // Handle card click to directly navigate to detailed view
    const handleCardClick = (tab, status = '') => {
        // Navigate directly to the tab
        setActiveTab(tab);
        
        // Set appropriate filters and fetch data
        if (tab === 'cheques') {
            setChequeStatusFilter(status);
            fetchCheques(1, '', status, '', '');
        } else if (tab === 'cashbook') {
            setCashbookStatusFilter(status);
            fetchCashbook(1, '', status, '', '');
        } else if (tab === 'documents') {
            setDocsStatusFilter(status);
            fetchDocuments(1, '', status, '', '');
        } else if (tab === 'register') {
            setRegisterStatusFilter(status);
            fetchRegister(1, '', status, '', '');
        }
    };

    // Handle branch click from Today's Status to navigate to Daily Documents
    const handleBranchClick = async (businessGroup, branch) => {
        // Switch to monthly tab
        setActiveTab('monthly');

        // Derive month/year from the selected report date, not from today
        const reportDate = new Date(selectedReportDate);
        const currentMonth = reportDate.getMonth() + 1;
        const currentYear = reportDate.getFullYear();
        
        // Set the filters to the clicked branch
        setSelectedBusinessGroup(businessGroup);
        setSelectedBranch(branch);
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
        
        // Fetch branches for the business group first
        await fetchBranches(businessGroup);
        
        // Fetch the document with explicit parameters
        fetchMonthlyDocument(businessGroup, branch, currentMonth, currentYear);
    };

    const renderCalendar = (report) => {
        const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
        const days = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const dayStatus = report.dailyStatuses?.find(ds => ds.day === day);
            const status = dayStatus?.status || 'NOT_RECEIVED';
            const isReceived = status === 'RECEIVED';
            const isNotApplicable = status === 'NOT_APPLICABLE';
            
            // Check who marked it
            const receptionistMarked = dayStatus?.receptionistMarkedDate;
            const accountsVerified = dayStatus?.accountsMarkedDate;
            
            // Determine visual state
            let statusClass = 'not-received';
            let statusIcon = '✗';
            let title = '';
            
            if (isNotApplicable) {
                // Not applicable - show gray with N/A
                statusClass = 'not-applicable';
                statusIcon = 'N/A';
                const reason = dayStatus?.notApplicableReason || 'Not Applicable';
                const isAuto = dayStatus?.isAutoMarkedNA ? ' (Auto)' : '';
                title = `${reason}${isAuto}`;
            } else if (isReceived && receptionistMarked) {
                if (accountsVerified) {
                    // Both marked - show green + orange ticks (verified)
                    statusClass = 'verified';
                    statusIcon = '✓✓';
                    title = 'Verified';
                } else {
                    // Only receptionist marked - show green tick
                    statusClass = 'receptionist-marked';
                    statusIcon = '✓';
                    title = 'Marked by Receptionist';
                }
            } else {
                title = 'Not Received';
            }
            
            days.push(
                <div key={day} className={`calendar-day ${statusClass}`} title={title}>
                    <div className="day-number">{day}</div>
                    <div className="day-status">{statusIcon}</div>
                </div>
            );
        }
        return <div className="calendar-grid">{days}</div>;
    };

    return (
        <div className="cheque-register-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="cheque-register-content">
                <div className="cheque-header manager-header">
                    <div className="header-logo-section">
                        <img src="/logo.png" alt="Logo" className="app-logo" />
                    </div>
                    <div className="header-text">
                        <h1>Management View</h1>
                        <p className="header-subtitle">Dashboard & Reports</p>
                    </div>
                    <div className="header-actions">
                        <UserProfileButton />
                        <div className="header-buttons">
                            <button
                                onClick={() => navigate('/purpose-selection')}
                                className="btn btn-secondary btn-sm"
                                title="Back to Purpose Selection"
                            >
                                ← Back
                            </button>
                            <button 
                                onClick={() => FEATURES.TASKS_ENABLED && navigate('/tasks')} 
                                className={`btn btn-success btn-sm task-button-with-notification ${!FEATURES.TASKS_ENABLED ? 'disabled-feature' : ''}`}
                                disabled={!FEATURES.TASKS_ENABLED}
                                title={!FEATURES.TASKS_ENABLED ? 'Coming Soon' : 'Tasks'}
                            >
                                <FiCheckSquare /> Tasks
                                {!FEATURES.TASKS_ENABLED && <span className="coming-soon-badge">Coming Soon</span>}
                                {FEATURES.TASKS_ENABLED && hasTaskNotifications && <span className="notification-dot"></span>}
                            </button>
                            <LogoutButton variant="secondary" className="btn-sm" />
                        </div>
                    </div>
                </div>

                <div className="cheque-tabs">
                    <button className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={() => setActiveTab('dashboard')}>
                        Dashboard Overview
                    </button>
                    <div className="modules-dropdown-container">
                        <button 
                            className={`tab-button dropdown-trigger ${activeTab !== 'dashboard' ? 'active' : ''}`}
                            onClick={() => setShowModulesDropdown(!showModulesDropdown)}>
                            Detailed Views {showModulesDropdown ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                        {showModulesDropdown && (
                            <div className="modules-dropdown-menu">
                                <button 
                                    className={`dropdown-item ${activeTab === 'cheques' ? 'active' : ''}`}
                                    onClick={() => { setActiveTab('cheques'); setShowModulesDropdown(false); }}>
                                    All Cheques
                                </button>
                                <button 
                                    className={`dropdown-item ${activeTab === 'cashbook' ? 'active' : ''}`}
                                    onClick={() => { setActiveTab('cashbook'); setShowModulesDropdown(false); }}>
                                    All Cash Payments
                                </button>
                                <button 
                                    className={`dropdown-item ${activeTab === 'documents' ? 'active' : ''}`}
                                    onClick={() => { setActiveTab('documents'); setShowModulesDropdown(false); }}>
                                    Document Log Book
                                </button>
                                <button 
                                    className={`dropdown-item ${activeTab === 'register' ? 'active' : ''}`}
                                    onClick={() => { setActiveTab('register'); setShowModulesDropdown(false); }}>
                                    Register Book
                                </button>
                                <button 
                                    className={`dropdown-item ${activeTab === 'monthly' ? 'active' : ''}`}
                                    onClick={() => { setActiveTab('monthly'); setShowModulesDropdown(false); }}>
                                    Daily Documents
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* TAB 1: DASHBOARD OVERVIEW */}
                {activeTab === 'dashboard' && (
                    <div className="tab-content">
                        {loadingDashboard ? (
                            <div className="loading-state">Loading dashboard statistics...</div>
                        ) : (
                            <>
                                {/* Daily Report Status Section */}
                                <div className="dashboard-section">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                                        <h2 className="section-title" style={{ marginBottom: 0, borderBottom: 'none' }}>
                                            Document Report Tracker
                                        </h2>
                                        <button className="btn btn-primary btn-sm" onClick={() => {
                                            fetchDailyReportStatus(selectedReportDate);
                                            fetchMonthlyStatuses(calendarMonth, calendarYear);
                                        }} disabled={loadingDailyReport || loadingCalendar}>
                                            <FiRefreshCw /> {(loadingDailyReport || loadingCalendar) ? 'Loading...' : 'Refresh'}
                                        </button>
                                    </div>

                                    {/* Calendar Picker - Always Visible */}
                                    <div style={{ marginBottom: '1rem' }}>
                                        {renderCalendarPicker()}
                                    </div>
                                    
                                    {loadingDailyReport && (
                                        <div style={{ textAlign: 'center', padding: '1rem', color: '#718096', fontSize: '0.95rem' }}>
                                            Loading report details...
                                        </div>
                                    )}

                                    {/* Modal for Daily Report Details */}
                                    {showReportModal && dailyReportStatus && dailyReportStatus.branches.length > 0 && (
                                        <div className="report-modal-overlay" onClick={() => setShowReportModal(false)}>
                                            <div className="report-modal-content" onClick={(e) => e.stopPropagation()}>
                                                <div className="report-modal-header">
                                                    <h3>
                                                        Report Status for: {new Date(dailyReportStatus.date).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                                                    </h3>
                                                    <button className="modal-close-btn" onClick={() => setShowReportModal(false)}>✕</button>
                                                </div>
                                                
                                                <div className="report-modal-body">
                                                    {/* Summary Cards */}
                                                    <div className="dashboard-cards" style={{ marginBottom: '2rem' }}>
                                                        <div className="dashboard-card card-collected">
                                                            <div className="card-content">
                                                                <h3>{dailyReportStatus.summary.completeCount}</h3>
                                                                <p>All Reports Received</p>
                                                                <small style={{ color: '#718096', fontSize: '0.85rem' }}>
                                                                    {dailyReportStatus.summary.completeCount} of {dailyReportStatus.summary.totalBranches} branches
                                                                </small>
                                                            </div>
                                                        </div>
                                                        <div className="dashboard-card card-warning">
                                                            <div className="card-content">
                                                                <h3>{dailyReportStatus.summary.partialCount}</h3>
                                                                <p>Partial Reports</p>
                                                                <small style={{ color: '#718096', fontSize: '0.85rem' }}>
                                                                    {dailyReportStatus.summary.partialCount} of {dailyReportStatus.summary.totalBranches} branches
                                                                </small>
                                                            </div>
                                                        </div>
                                                        <div className="dashboard-card card-pending">
                                                            <div className="card-content">
                                                                <h3>{dailyReportStatus.summary.noneCount}</h3>
                                                                <p>No Reports Yet</p>
                                                                <small style={{ color: '#718096', fontSize: '0.85rem' }}>
                                                                    {dailyReportStatus.summary.noneCount} of {dailyReportStatus.summary.totalBranches} branches
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Traffic Light Grid with Pending Indicator */}
                                                    <div className="today-status-grid">
                                                        {Object.entries(
                                                            dailyReportStatus.branches.reduce((acc, branch) => {
                                                                const normalizedGroup = branch.businessGroup.trim();
                                                                if (!acc[normalizedGroup]) acc[normalizedGroup] = [];
                                                                acc[normalizedGroup].push(branch);
                                                                return acc;
                                                            }, {})
                                                        ).sort(([a], [b]) => a.localeCompare(b)).map(([businessGroup, branches]) => (
                                                            <div key={businessGroup} className="business-group-section">
                                                                <h3 className="business-group-title">{businessGroup}</h3>
                                                                <div className="branch-status-grid">
                                                                    {branches.sort((a, b) => a.branch.localeCompare(b.branch)).map((branch) => (
                                                                        <div 
                                                                            key={`${branch.businessGroup}-${branch.branch}`}
                                                                            className={`branch-status-item status-${branch.status}`}
                                                                            title={`${branch.branch}: ${branch.receivedReports}/${branch.totalReports} reports (${branch.percentage}%) - Click to view details`}
                                                                            onClick={() => {
                                                                                setShowReportModal(false);
                                                                                handleBranchClick(branch.businessGroup, branch.branch);
                                                                            }}
                                                                        >
                                                                            <div className="status-indicator">
                                                                                {branch.status === 'complete' && '●'}
                                                                                {branch.status === 'partial' && '◐'}
                                                                                {branch.status === 'none' && '○'}
                                                                            </div>
                                                                            <div className="branch-name">
                                                                                {branch.branch}
                                                                                {branch.status !== 'complete' && (
                                                                                    <span className="pending-indicator" title="Reports pending">⚠️</span>
                                                                                )}
                                                                            </div>
                                                                            <div className="branch-percentage">{branch.percentage}%</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Legend */}
                                                    <div className="status-legend">
                                                        <div className="legend-item">
                                                            <span className="legend-icon status-complete">●</span>
                                                            <span>All Reports Received</span>
                                                        </div>
                                                        <div className="legend-item">
                                                            <span className="legend-icon status-partial">◐</span>
                                                            <span>Partial Reports</span>
                                                        </div>
                                                        <div className="legend-item">
                                                            <span className="legend-icon status-none">○</span>
                                                            <span>No Reports Yet</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>



                                {/* Date Range Filter for Other Sections */}
                                <div className="dashboard-filters">
                                    <label>Date Range for Statistics Below:</label>
                                    <input type="date" value={dashboardFromDate}
                                        onChange={(e) => { setDashboardFromDate(e.target.value); fetchDashboardStats(e.target.value, dashboardToDate); }}
                                        className="date-input" />
                                    <span>to</span>
                                    <input type="date" value={dashboardToDate}
                                        onChange={(e) => { setDashboardToDate(e.target.value); fetchDashboardStats(dashboardFromDate, e.target.value); }}
                                        className="date-input" />
                                    <button className="btn btn-primary btn-sm" onClick={() => fetchDashboardStats(dashboardFromDate, dashboardToDate)}>
                                        <FiRefreshCw /> Refresh
                                    </button>
                                </div>

                                {/* Cheques Section */}
                                <div className="dashboard-section">
                                    <h2 className="section-title">Cheque Register</h2>
                                    <div className="dashboard-cards">
                                        <div className="dashboard-card card-issued clickable-card" 
                                             onClick={() => handleCardClick('cheques', '')}
                                             title="Click to view all submitted cheques">
                                            <div className="card-content">
                                                <h3>{dashboardStats.cheques.submitted.count}</h3>
                                                <p>Cheques Submitted</p>
                                                <div className="card-amount">{formatAmount(dashboardStats.cheques.submitted.amount)}</div>
                                            </div>
                                        </div>
                                        <div className="dashboard-card card-collected clickable-card" 
                                             onClick={() => handleCardClick('cheques', 'collected')}
                                             title="Click to view cheques issued to suppliers">
                                            <div className="card-content">
                                                <h3>{dashboardStats.cheques.collected.count}</h3>
                                                <p>Cheques Issued to Supplier</p>
                                                <div className="card-amount">{formatAmount(dashboardStats.cheques.collected.amount)}</div>
                                            </div>
                                        </div>
                                        <div className="dashboard-card card-pending clickable-card" 
                                             onClick={() => handleCardClick('cheques', 'pending')}
                                             title="Click to view cheques pending issuance">
                                            <div className="card-content">
                                                <h3>{dashboardStats.cheques.pending.count}</h3>
                                                <p>Cheques Pending Issuance</p>
                                                <div className="card-amount">{formatAmount(dashboardStats.cheques.pending.amount)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Cash Payments Section */}
                                <div className="dashboard-section">
                                    <h2 className="section-title">Cash Payments</h2>
                                    <div className="dashboard-cards">
                                        <div className="dashboard-card card-issued clickable-card" 
                                             onClick={() => handleCardClick('cashbook', '')}
                                             title="Click to view all submitted cash payments">
                                            <div className="card-content">
                                                <h3>{dashboardStats.cashPayments.submitted.count}</h3>
                                                <p>Cash Submitted</p>
                                                <div className="card-amount">{formatAmount(dashboardStats.cashPayments.submitted.amount)}</div>
                                            </div>
                                        </div>
                                        <div className="dashboard-card card-collected clickable-card" 
                                             onClick={() => handleCardClick('cashbook', 'collected')}
                                             title="Click to view cash issued to suppliers">
                                            <div className="card-content">
                                                <h3>{dashboardStats.cashPayments.collected.count}</h3>
                                                <p>Cash Issued to Supplier</p>
                                                <div className="card-amount">{formatAmount(dashboardStats.cashPayments.collected.amount)}</div>
                                            </div>
                                        </div>
                                        <div className="dashboard-card card-pending clickable-card" 
                                             onClick={() => handleCardClick('cashbook', 'pending')}
                                             title="Click to view cash pending issuance">
                                            <div className="card-content">
                                                <h3>{dashboardStats.cashPayments.pending.count}</h3>
                                                <p>Cash Pending Issuance</p>
                                                <div className="card-amount">{formatAmount(dashboardStats.cashPayments.pending.amount)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Register Book Section */}
                                <div className="dashboard-section">
                                    <h2 className="section-title">Register Book</h2>
                                    <div className="dashboard-cards">
                                        <div className="dashboard-card card-info clickable-card" 
                                             onClick={() => handleCardClick('register', 'CHECKED_IN')}
                                             title="Click to view all check-ins">
                                            <div className="card-content">
                                                <h3>{dashboardStats.registerBook.checkIns}</h3>
                                                <p>Check-Ins</p>
                                            </div>
                                        </div>
                                        <div className="dashboard-card card-info clickable-card" 
                                             onClick={() => handleCardClick('register', 'CHECKED_OUT')}
                                             title="Click to view all check-outs">
                                            <div className="card-content">
                                                <h3>{dashboardStats.registerBook.checkOuts}</h3>
                                                <p>Check-Outs</p>
                                            </div>
                                        </div>
                                        <div className="dashboard-card card-warning clickable-card" 
                                             onClick={() => handleCardClick('register', 'CHECKED_IN')}
                                             title="Click to view currently checked-in visitors">
                                            <div className="card-content">
                                                <h3>{dashboardStats.registerBook.currentlyCheckedIn}</h3>
                                                <p>Currently Inside</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Document Log Book Section */}
                                <div className="dashboard-section">
                                    <h2 className="section-title">Document Log Book</h2>
                                    <div className="dashboard-cards">
                                        <div className="dashboard-card card-info clickable-card" 
                                             onClick={() => handleCardClick('documents', '')}
                                             title="Click to view all documents">
                                            <div className="card-content">
                                                <h3>{dashboardStats.documentLogBook.created}</h3>
                                                <p>Documents Created</p>
                                            </div>
                                        </div>
                                        <div className="dashboard-card card-collected clickable-card" 
                                             onClick={() => handleCardClick('documents', 'completed')}
                                             title="Click to view all signed documents">
                                            <div className="card-content">
                                                <h3>{dashboardStats.documentLogBook.completed}</h3>
                                                <p>Documents Signed</p>
                                            </div>
                                        </div>
                                        <div className="dashboard-card card-pending clickable-card" 
                                             onClick={() => handleCardClick('documents', 'pending')}
                                             title="Click to view pending documents">
                                            <div className="card-content">
                                                <h3>{dashboardStats.documentLogBook.pending}</h3>
                                                <p>Pending Signature</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                

                {/* TAB 2: ALL CHEQUES */}
                {activeTab === 'cheques' && (
                    <div className="tab-content">
                        <div className="search-filter-bar enhanced">
                            <input type="text" placeholder="Search cheques..." value={searchCheques}
                                onChange={(e) => { 
                                    setSearchCheques(e.target.value); 
                                    if (chequeViewMode === 'list') {
                                        fetchCheques(1, e.target.value, chequeStatusFilter, chequeCompanyFilter, chequeSupplierFilter, chequeFromDate, chequeToDate);
                                    }
                                }}
                                className="search-input" />
                            
                            <select 
                                value={chequeStatusFilter}
                                onChange={(e) => {
                                    setChequeStatusFilter(e.target.value);
                                    if (chequeViewMode === 'list') {
                                        fetchCheques(1, searchCheques, e.target.value, chequeCompanyFilter, chequeSupplierFilter, chequeFromDate, chequeToDate);
                                    } else {
                                        fetchGroupedCheques();
                                    }
                                }}
                                className="filter-select"
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="collected">Collected</option>
                                <option value="returned">Returned</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                            
                            <select 
                                value={chequeCompanyFilter}
                                onChange={(e) => {
                                    setChequeCompanyFilter(e.target.value);
                                    if (chequeViewMode === 'list') {
                                        fetchCheques(1, searchCheques, chequeStatusFilter, e.target.value, chequeSupplierFilter, chequeFromDate, chequeToDate);
                                    } else {
                                        fetchGroupedCheques();
                                    }
                                }}
                                className="filter-select"
                            >
                                <option value="">All Companies</option>
                                <option value="BRADMA QATAR FOOD (BQF)">BQF</option>
                                <option value="AHLA MART TRADING">AMT</option>
                                <option value="BRADMA QATAR WLL">BMMW</option>
                                <option value="CENTRAL AUTO PARTS (CAP)">CAP</option>
                            </select>
                            
                            <select 
                                value={chequeSupplierFilter}
                                onChange={(e) => {
                                    setChequeSupplierFilter(e.target.value);
                                    if (chequeViewMode === 'list') {
                                        fetchCheques(1, searchCheques, chequeStatusFilter, chequeCompanyFilter, e.target.value, chequeFromDate, chequeToDate);
                                    } else {
                                        fetchGroupedCheques();
                                    }
                                }}
                                className="filter-select"
                            >
                                <option value="">All Suppliers</option>
                                {chequeSuppliersList.map((supplier, idx) => (
                                    <option key={idx} value={supplier}>{supplier}</option>
                                ))}
                            </select>
                            
                            <div className="date-filters">
                                <input type="date" value={chequeFromDate}
                                    onChange={(e) => { 
                                        setChequeFromDate(e.target.value); 
                                        if (chequeViewMode === 'list') {
                                            fetchCheques(1, searchCheques, chequeStatusFilter, chequeCompanyFilter, chequeSupplierFilter, e.target.value, chequeToDate);
                                        } else {
                                            fetchGroupedCheques();
                                        }
                                    }}
                                    className="date-input" />
                                <input type="date" value={chequeToDate}
                                    onChange={(e) => { 
                                        setChequeToDate(e.target.value); 
                                        if (chequeViewMode === 'list') {
                                            fetchCheques(1, searchCheques, chequeStatusFilter, chequeCompanyFilter, chequeSupplierFilter, chequeFromDate, e.target.value);
                                        } else {
                                            fetchGroupedCheques();
                                        }
                                    }}
                                    className="date-input" />
                            </div>
                            
                            <div className="view-mode-toggle">
                                <button 
                                    className={`btn-view-mode ${chequeViewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => {
                                        setChequeViewMode('list');
                                        fetchCheques(currentChequePage, searchCheques, chequeStatusFilter, chequeCompanyFilter, chequeSupplierFilter, chequeFromDate, chequeToDate);
                                    }}
                                    title="List View"
                                >
                                    📋 List
                                </button>
                                <button 
                                    className={`btn-view-mode ${chequeViewMode === 'grouped' ? 'active' : ''}`}
                                    onClick={() => {
                                        setChequeViewMode('grouped');
                                        fetchGroupedCheques();
                                    }}
                                    title="Grouped View"
                                >
                                    📊 Group
                                </button>
                            </div>
                        </div>

                        {chequeViewMode === 'grouped' && (
                            <div className="group-by-options">
                                <label>Group By:</label>
                                <select 
                                    value={chequeGroupByField}
                                    onChange={(e) => {
                                        setChequeGroupByField(e.target.value);
                                        fetchGroupedCheques(e.target.value);
                                    }}
                                    className="group-by-select"
                                >
                                    <option value="company">Company</option>
                                    <option value="supplier">Supplier</option>
                                    <option value="status">Status</option>
                                </select>
                            </div>
                        )}
                        
                        {loadingCheques ? (
                            <div className="loading-state">Loading cheques...</div>
                        ) : chequeViewMode === 'list' ? (
                            allCheques.length === 0 ? (
                                <div className="empty-state"><p>No cheques found</p></div>
                            ) : (
                                <>
                                    <div className="cheques-table-container">
                                        <table className="cheques-table">
                                            <thead>
                                                <tr>
                                                    <th>👁️</th>
                                                    <th>Cheque No.</th>
                                                    <th>Supplier</th>
                                                    <th>Company</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Issued By</th>
                                                    <th>Collected By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allCheques.map((cheque) => (
                                                    <tr key={cheque._id}>
                                                        <td>
                                                            <button className="btn-view-icon"
                                                                onClick={() => setViewChequeModal(cheque)}
                                                                title="View Details">
                                                                👁️
                                                            </button>
                                                        </td>
                                                        <td>{cheque.chequeNumber}</td>
                                                        <td>{cheque.supplierName}</td>
                                                        <td>{getCompanyShortForm(cheque.company)}</td>
                                                        <td className="amount-cell">{formatAmount(cheque.amount)}</td>
                                                        <td>
                                                            <span className={`status-badge status-${cheque.status}`}>
                                                                {cheque.status === 'pending' && '🟡 Pending'}
                                                                {cheque.status === 'collected' && '🟢 Collected'}
                                                                {cheque.status === 'returned' && '🔵 Returned'}
                                                                {cheque.status === 'cancelled' && '🔴 Cancelled'}
                                                            </span>
                                                        </td>
                                                        <td>{cheque.issuedBy}</td>
                                                        <td>{cheque.collectedBy || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalChequePages > 1 && (
                                        <div className="pagination">
                                            <button onClick={() => fetchCheques(currentChequePage - 1, searchCheques, chequeStatusFilter, chequeCompanyFilter, chequeSupplierFilter, chequeFromDate, chequeToDate)}
                                                disabled={currentChequePage === 1} className="pagination-btn">Previous</button>
                                            <span className="pagination-info">Page {currentChequePage} of {totalChequePages}</span>
                                            <button onClick={() => fetchCheques(currentChequePage + 1, searchCheques, chequeStatusFilter, chequeCompanyFilter, chequeSupplierFilter, chequeFromDate, chequeToDate)}
                                                disabled={currentChequePage === totalChequePages} className="pagination-btn">Next</button>
                                        </div>
                                    )}
                                </>
                            )
                        ) : (
                            chequeGroupedData.length === 0 ? (
                                <div className="empty-state"><p>No cheques found</p></div>
                            ) : (
                                <div className="grouped-view-container">
                                    {chequeGroupedData.map((group, idx) => (
                                        <div key={idx} className="group-card">
                                            <div 
                                                className="group-header"
                                                onClick={() => toggleChequeGroup(group._id)}
                                            >
                                                <div className="group-title">
                                                    <span className="expand-icon">
                                                        {chequeExpandedGroups.has(group._id) ? '▼' : '▶'}
                                                    </span>
                                                    <h3>{group._id || 'Unknown'}</h3>
                                                </div>
                                                <div className="group-stats">
                                                    <div className="stat-item">
                                                        <span className="stat-label">Total:</span>
                                                        <span className="stat-value">{group.totalCount} cheques</span>
                                                    </div>
                                                    <div className="stat-item">
                                                        <span className="stat-label">Amount:</span>
                                                        <span className="stat-value">{formatAmount(group.totalAmount)}</span>
                                                    </div>
                                                    <div className="stat-breakdown">
                                                        {group.pendingCount > 0 && (
                                                            <span className="badge badge-pending">
                                                                {group.pendingCount} Pending ({formatAmount(group.pendingAmount)})
                                                            </span>
                                                        )}
                                                        {group.collectedCount > 0 && (
                                                            <span className="badge badge-collected">
                                                                {group.collectedCount} Collected ({formatAmount(group.collectedAmount)})
                                                            </span>
                                                        )}
                                                        {group.returnedCount > 0 && (
                                                            <span className="badge badge-returned">
                                                                {group.returnedCount} Returned ({formatAmount(group.returnedAmount)})
                                                            </span>
                                                        )}
                                                        {group.cancelledCount > 0 && (
                                                            <span className="badge badge-cancelled">
                                                                {group.cancelledCount} Cancelled
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {chequeExpandedGroups.has(group._id) && (
                                                <div className="group-details">
                                                    {loadingChequeGroups.has(`${group._id}-${chequeGroupPages[group._id] || 1}`) ? (
                                                        <div className="loading-spinner">Loading cheques...</div>
                                                    ) : chequeGroupDetails[group._id] ? (
                                                        <>
                                                            <table className="cheques-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>👁️</th>
                                                                        <th>Cheque No.</th>
                                                                        <th>Supplier</th>
                                                                        <th>Company</th>
                                                                        <th>Amount</th>
                                                                        <th>Status</th>
                                                                        <th>Date</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {chequeGroupDetails[group._id].cheques.map((cheque) => (
                                                                        <tr key={cheque._id}>
                                                                            <td>
                                                                                <button 
                                                                                    className="btn-view-icon"
                                                                                    onClick={() => setViewChequeModal(cheque)}
                                                                                    title="View Details"
                                                                                >
                                                                                    👁️
                                                                                </button>
                                                                            </td>
                                                                            <td>{cheque.chequeNumber}</td>
                                                                            <td>{cheque.supplierName}</td>
                                                                            <td>{getCompanyShortForm(cheque.company)}</td>
                                                                            <td>{formatAmount(cheque.amount)}</td>
                                                                            <td>
                                                                                <span className={`status-badge status-${cheque.status}`}>
                                                                                    {cheque.status === 'pending' && '🟡 Pending'}
                                                                                    {cheque.status === 'collected' && '🟢 Collected'}
                                                                                    {cheque.status === 'returned' && '🔵 Returned'}
                                                                                    {cheque.status === 'cancelled' && '🔴 Cancelled'}
                                                                                </span>
                                                                            </td>
                                                                            <td>{formatDate(cheque.entryDate)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                            {chequeGroupDetails[group._id].totalPages > 1 && (
                                                                <div className="pagination">
                                                                    <button 
                                                                        onClick={() => handleChequeGroupPageChange(group._id, (chequeGroupPages[group._id] || 1) - 1)}
                                                                        disabled={!chequeGroupPages[group._id] || chequeGroupPages[group._id] === 1}
                                                                    >
                                                                        Previous
                                                                    </button>
                                                                    <span>Page {chequeGroupPages[group._id] || 1} of {chequeGroupDetails[group._id].totalPages}</span>
                                                                    <button 
                                                                        onClick={() => handleChequeGroupPageChange(group._id, (chequeGroupPages[group._id] || 1) + 1)}
                                                                        disabled={chequeGroupPages[group._id] >= chequeGroupDetails[group._id].totalPages}
                                                                    >
                                                                        Next
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="no-data">No cheques found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                )}


                {/* TAB 3: ALL CASHBOOK */}
                {activeTab === 'cashbook' && (
                    <div className="tab-content">
                        <div className="search-filter-bar enhanced">
                            <input type="text" placeholder="Search cash payments..." value={searchCashbook}
                                onChange={(e) => { 
                                    setSearchCashbook(e.target.value); 
                                    if (cashbookViewMode === 'list') {
                                        fetchCashbook(1, e.target.value, cashbookStatusFilter, cashbookCompanyFilter, cashbookSupplierFilter, cashbookFromDate, cashbookToDate);
                                    }
                                }}
                                className="search-input" />
                            
                            <select 
                                value={cashbookStatusFilter}
                                onChange={(e) => {
                                    setCashbookStatusFilter(e.target.value);
                                    if (cashbookViewMode === 'list') {
                                        fetchCashbook(1, searchCashbook, e.target.value, cashbookCompanyFilter, cashbookSupplierFilter, cashbookFromDate, cashbookToDate);
                                    } else {
                                        fetchGroupedCashbook();
                                    }
                                }}
                                className="filter-select"
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending Issuance</option>
                                <option value="collected">Issued to Supplier</option>
                                <option value="returned">Returned</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                            
                            <select 
                                value={cashbookCompanyFilter}
                                onChange={(e) => {
                                    setCashbookCompanyFilter(e.target.value);
                                    if (cashbookViewMode === 'list') {
                                        fetchCashbook(1, searchCashbook, cashbookStatusFilter, e.target.value, cashbookSupplierFilter, cashbookFromDate, cashbookToDate);
                                    } else {
                                        fetchGroupedCashbook();
                                    }
                                }}
                                className="filter-select"
                            >
                                <option value="">All Companies</option>
                                <option value="BRADMA QATAR FOOD (BQF)">BQF</option>
                                <option value="AHLA MART TRADING">AMT</option>
                                <option value="BRADMA QATAR WLL">BMMW</option>
                                <option value="CENTRAL AUTO PARTS (CAP)">CAP</option>
                            </select>
                            
                            <select 
                                value={cashbookSupplierFilter}
                                onChange={(e) => {
                                    setCashbookSupplierFilter(e.target.value);
                                    if (cashbookViewMode === 'list') {
                                        fetchCashbook(1, searchCashbook, cashbookStatusFilter, cashbookCompanyFilter, e.target.value, cashbookFromDate, cashbookToDate);
                                    } else {
                                        fetchGroupedCashbook();
                                    }
                                }}
                                className="filter-select"
                            >
                                <option value="">All Suppliers</option>
                                {cashbookSuppliersList.map((supplier, idx) => (
                                    <option key={idx} value={supplier}>{supplier}</option>
                                ))}
                            </select>
                            
                            <div className="date-filters">
                                <input type="date" value={cashbookFromDate}
                                    onChange={(e) => { 
                                        setCashbookFromDate(e.target.value); 
                                        if (cashbookViewMode === 'list') {
                                            fetchCashbook(1, searchCashbook, cashbookStatusFilter, cashbookCompanyFilter, cashbookSupplierFilter, e.target.value, cashbookToDate);
                                        } else {
                                            fetchGroupedCashbook();
                                        }
                                    }}
                                    className="date-input" />
                                <input type="date" value={cashbookToDate}
                                    onChange={(e) => { 
                                        setCashbookToDate(e.target.value); 
                                        if (cashbookViewMode === 'list') {
                                            fetchCashbook(1, searchCashbook, cashbookStatusFilter, cashbookCompanyFilter, cashbookSupplierFilter, cashbookFromDate, e.target.value);
                                        } else {
                                            fetchGroupedCashbook();
                                        }
                                    }}
                                    className="date-input" />
                            </div>
                            
                            <div className="view-mode-toggle">
                                <button 
                                    className={`btn-view-mode ${cashbookViewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => {
                                        setCashbookViewMode('list');
                                        fetchCashbook(currentCashbookPage, searchCashbook, cashbookStatusFilter, cashbookCompanyFilter, cashbookSupplierFilter, cashbookFromDate, cashbookToDate);
                                    }}
                                    title="List View"
                                >
                                    📋 List
                                </button>
                                <button 
                                    className={`btn-view-mode ${cashbookViewMode === 'grouped' ? 'active' : ''}`}
                                    onClick={() => {
                                        setCashbookViewMode('grouped');
                                        fetchGroupedCashbook();
                                    }}
                                    title="Grouped View"
                                >
                                    📊 Group
                                </button>
                            </div>
                        </div>

                        {cashbookViewMode === 'grouped' && (
                            <div className="group-by-options">
                                <label>Group By:</label>
                                <select 
                                    value={cashbookGroupByField}
                                    onChange={(e) => {
                                        setCashbookGroupByField(e.target.value);
                                        fetchGroupedCashbook(e.target.value);
                                    }}
                                    className="group-by-select"
                                >
                                    <option value="company">Company</option>
                                    <option value="supplier">Supplier</option>
                                    <option value="status">Status</option>
                                </select>
                            </div>
                        )}
                        
                        {loadingCashbook ? (
                            <div className="loading-state">Loading cash payments...</div>
                        ) : cashbookViewMode === 'list' ? (
                            allCashbook.length === 0 ? (
                                <div className="empty-state"><p>No cash payments found</p></div>
                            ) : (
                                <>
                                    <div className="cheques-table-container">
                                        <table className="cheques-table">
                                            <thead>
                                                <tr>
                                                    <th>👁️</th>
                                                    <th>Voucher No.</th>
                                                    <th>Supplier</th>
                                                    <th>Company</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Issued By</th>
                                                    <th>Collected By</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allCashbook.map((item) => (
                                                    <tr key={item._id}>
                                                        <td>
                                                            <button className="btn-view-icon"
                                                                onClick={() => setViewChequeModal(item)}
                                                                title="View Details">
                                                                👁️
                                                            </button>
                                                        </td>
                                                        <td>{item.voucherNumber}</td>
                                                        <td>{item.supplierName}</td>
                                                        <td>{getCompanyShortForm(item.company)}</td>
                                                        <td className="amount-cell">{formatAmount(item.amount)}</td>
                                                        <td>
                                                            <span className={`status-badge status-${item.status}`}>
                                                                {item.status === 'pending' ? '🟡 Pending Issuance' : 
                                                                 item.status === 'collected' ? '🟢 Issued to Supplier' : 
                                                                 item.status === 'returned' ? '🔴 Returned' :
                                                                 '⚫ Cancelled'}
                                                            </span>
                                                        </td>
                                                        <td>{item.issuedBy}</td>
                                                        <td>{item.collectedBy || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalCashbookPages > 1 && (
                                        <div className="pagination">
                                            <button onClick={() => fetchCashbook(currentCashbookPage - 1, searchCashbook, cashbookStatusFilter, cashbookCompanyFilter, cashbookSupplierFilter, cashbookFromDate, cashbookToDate)}
                                                disabled={currentCashbookPage === 1} className="pagination-btn">Previous</button>
                                            <span className="pagination-info">Page {currentCashbookPage} of {totalCashbookPages}</span>
                                            <button onClick={() => fetchCashbook(currentCashbookPage + 1, searchCashbook, cashbookStatusFilter, cashbookCompanyFilter, cashbookSupplierFilter, cashbookFromDate, cashbookToDate)}
                                                disabled={currentCashbookPage === totalCashbookPages} className="pagination-btn">Next</button>
                                        </div>
                                    )}
                                </>
                            )
                        ) : (
                            cashbookGroupedData.length === 0 ? (
                                <div className="empty-state"><p>No cash payments found</p></div>
                            ) : (
                                <div className="grouped-view-container">
                                    {cashbookGroupedData.map((group, idx) => (
                                        <div key={idx} className="group-card">
                                            <div 
                                                className="group-header"
                                                onClick={() => toggleCashbookGroup(group._id)}
                                            >
                                                <div className="group-title">
                                                    <span className="expand-icon">
                                                        {cashbookExpandedGroups.has(group._id) ? '▼' : '▶'}
                                                    </span>
                                                    <h3>{group._id || 'Unknown'}</h3>
                                                </div>
                                                <div className="group-stats">
                                                    <div className="stat-item">
                                                        <span className="stat-label">Total:</span>
                                                        <span className="stat-value">{group.totalCount} payments</span>
                                                    </div>
                                                    <div className="stat-item">
                                                        <span className="stat-label">Amount:</span>
                                                        <span className="stat-value">{formatAmount(group.totalAmount)}</span>
                                                    </div>
                                                    <div className="stat-breakdown">
                                                        {group.issuedCount > 0 && (
                                                            <span className="badge badge-pending">
                                                                {group.issuedCount} Issued ({formatAmount(group.issuedAmount)})
                                                            </span>
                                                        )}
                                                        {group.collectedCount > 0 && (
                                                            <span className="badge badge-collected">
                                                                {group.collectedCount} Collected ({formatAmount(group.collectedAmount)})
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {cashbookExpandedGroups.has(group._id) && (
                                                <div className="group-details">
                                                    {loadingCashbookGroups.has(`${group._id}-${cashbookGroupPages[group._id] || 1}`) ? (
                                                        <div style={{padding: '2rem', textAlign: 'center'}}>
                                                            <p>Loading...</p>
                                                        </div>
                                                    ) : cashbookGroupDetails[group._id] ? (
                                                        <>
                                                            <table className="cheques-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>👁️</th>
                                                                        <th>Voucher No.</th>
                                                                        <th>Supplier</th>
                                                                        <th>Company</th>
                                                                        <th>Amount</th>
                                                                        <th>Status</th>
                                                                        <th>Date</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {cashbookGroupDetails[group._id].cashbooks.map((cashbook) => (
                                                                        <tr key={cashbook._id}>
                                                                            <td>
                                                                                <button 
                                                                                    className="btn-view-icon"
                                                                                    onClick={() => setViewChequeModal(cashbook)}
                                                                                    title="View Details"
                                                                                >
                                                                                    👁️
                                                                                </button>
                                                                            </td>
                                                                            <td>{cashbook.voucherNumber}</td>
                                                                            <td>{cashbook.supplierName}</td>
                                                                            <td>{getCompanyShortForm(cashbook.company)}</td>
                                                                            <td>{formatAmount(cashbook.amount)}</td>
                                                                            <td>
                                                                                <span className={`status-badge status-${cashbook.status}`}>
                                                                                    {cashbook.status === 'pending' ? '🟡 Pending Issuance' : 
                                                                                     cashbook.status === 'collected' ? '🟢 Issued to Supplier' : 
                                                                                     cashbook.status === 'returned' ? '🔴 Returned' :
                                                                                     '⚫ Cancelled'}
                                                                                </span>
                                                                            </td>
                                                                            <td>{formatDate(cashbook.entryDate)}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                            {cashbookGroupDetails[group._id].totalPages > 1 && (
                                                                <div className="pagination">
                                                                    <button 
                                                                        onClick={() => handleCashbookGroupPageChange(group._id, (cashbookGroupPages[group._id] || 1) - 1)}
                                                                        disabled={!cashbookGroupPages[group._id] || cashbookGroupPages[group._id] === 1}
                                                                        className="pagination-btn"
                                                                    >
                                                                        Previous
                                                                    </button>
                                                                    <span className="pagination-info">
                                                                        Page {cashbookGroupPages[group._id] || 1} of {cashbookGroupDetails[group._id].totalPages}
                                                                    </span>
                                                                    <button 
                                                                        onClick={() => handleCashbookGroupPageChange(group._id, (cashbookGroupPages[group._id] || 1) + 1)}
                                                                        disabled={cashbookGroupPages[group._id] >= cashbookGroupDetails[group._id].totalPages}
                                                                        className="pagination-btn"
                                                                    >
                                                                        Next
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div style={{padding: '2rem', textAlign: 'center'}}>
                                                            <p>No data available</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                )}

                {/* TAB 4: DOCUMENT LOG BOOK */}
                {activeTab === 'documents' && (
                    <div className="tab-content">
                        <div className="search-filter-bar">
                            <input type="text" placeholder="Search documents..." value={searchDocs}
                                onChange={(e) => { setSearchDocs(e.target.value); fetchDocuments(1, e.target.value, docsStatusFilter, docsFromDate, docsToDate); }}
                                className="search-input" />
                            <div className="date-filters">
                                <input type="date" placeholder="From Date" value={docsFromDate}
                                    onChange={(e) => { setDocsFromDate(e.target.value); fetchDocuments(1, searchDocs, docsStatusFilter, e.target.value, docsToDate); }}
                                    className="date-input" />
                                <input type="date" placeholder="To Date" value={docsToDate}
                                    onChange={(e) => { setDocsToDate(e.target.value); fetchDocuments(1, searchDocs, docsStatusFilter, docsFromDate, e.target.value); }}
                                    className="date-input" />
                            </div>
                            <div className="status-filters">
                                <button className={`filter-btn ${docsStatusFilter === '' ? 'active' : ''}`}
                                    onClick={() => { setDocsStatusFilter(''); fetchDocuments(1, searchDocs, '', docsFromDate, docsToDate); }}>All</button>
                                <button className={`filter-btn ${docsStatusFilter === 'pending' ? 'active' : ''}`}
                                    onClick={() => { setDocsStatusFilter('pending'); fetchDocuments(1, searchDocs, 'pending', docsFromDate, docsToDate); }}>Pending</button>
                                <button className={`filter-btn ${docsStatusFilter === 'completed' ? 'active' : ''}`}
                                    onClick={() => { setDocsStatusFilter('completed'); fetchDocuments(1, searchDocs, 'completed', docsFromDate, docsToDate); }}>Completed</button>
                            </div>
                        </div>
                        
                        {loadingDocs ? (
                            <div className="loading-state">Loading documents...</div>
                        ) : completedDocs.length === 0 ? (
                            <div className="empty-state"><p>No documents found</p></div>
                        ) : (
                            <>
                                <div className="cheques-table-container">
                                    <table className="cheques-table">
                                        <thead>
                                            <tr>
                                                <th>Entry Date</th>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Documents</th>
                                                <th>Entered By</th>
                                                <th>Verifications</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {completedDocs.map((entry) => (
                                                <tr key={entry._id}>
                                                    <td>{formatDateTime(entry.entryDate)}</td>
                                                    <td>{entry.from}</td>
                                                    <td>{entry.to}</td>
                                                    <td className="documents-cell">{entry.documents}</td>
                                                    <td>{entry.enteredBy}</td>
                                                    <td>{entry.verifications?.length || 0} / {(entry.fromUserId ? 1 : 0) + (entry.toUserId ? 1 : 0) + (entry.additionalVerifiers?.length || 0)}</td>
                                                    <td>
                                                        <span className={`status-badge ${
                                                            entry.status === 'PENDING_VERIFICATION' ? 'status-pending' : 
                                                            entry.status === 'PARTIALLY_VERIFIED' ? 'status-partial' : 
                                                            'status-collected'
                                                        }`}>
                                                            {entry.status === 'PENDING_VERIFICATION' ? 'Pending' : 
                                                             entry.status === 'PARTIALLY_VERIFIED' ? 'Partial' : 
                                                             'Completed'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {totalDocsPages > 1 && (
                                    <div className="pagination">
                                        <button onClick={() => fetchDocuments(currentDocsPage - 1, searchDocs, docsStatusFilter, docsFromDate, docsToDate)}
                                            disabled={currentDocsPage === 1} className="pagination-btn">Previous</button>
                                        <span className="pagination-info">Page {currentDocsPage} of {totalDocsPages}</span>
                                        <button onClick={() => fetchDocuments(currentDocsPage + 1, searchDocs, docsStatusFilter, docsFromDate, docsToDate)}
                                            disabled={currentDocsPage === totalDocsPages} className="pagination-btn">Next</button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* TAB 5: REGISTER BOOK */}
                {activeTab === 'register' && (
                    <div className="tab-content">
                        <div className="search-filter-bar">
                            <input type="text" placeholder="Search register entries..." value={searchRegister}
                                onChange={(e) => { setSearchRegister(e.target.value); fetchRegister(1, e.target.value, registerStatusFilter, registerFromDate, registerToDate); }}
                                className="search-input" />
                            <div className="date-filters">
                                <input type="date" placeholder="From Date" value={registerFromDate}
                                    onChange={(e) => { setRegisterFromDate(e.target.value); fetchRegister(1, searchRegister, registerStatusFilter, e.target.value, registerToDate); }}
                                    className="date-input" />
                                <input type="date" placeholder="To Date" value={registerToDate}
                                    onChange={(e) => { setRegisterToDate(e.target.value); fetchRegister(1, searchRegister, registerStatusFilter, registerFromDate, e.target.value); }}
                                    className="date-input" />
                            </div>
                            <div className="status-filters">
                                <button className={`filter-btn ${registerStatusFilter === '' ? 'active' : ''}`}
                                    onClick={() => { setRegisterStatusFilter(''); fetchRegister(1, searchRegister, '', registerFromDate, registerToDate); }}>All</button>
                                <button className={`filter-btn ${registerStatusFilter === 'CHECKED_IN' ? 'active' : ''}`}
                                    onClick={() => { setRegisterStatusFilter('CHECKED_IN'); fetchRegister(1, searchRegister, 'CHECKED_IN', registerFromDate, registerToDate); }}>Checked In</button>
                                <button className={`filter-btn ${registerStatusFilter === 'CHECKED_OUT' ? 'active' : ''}`}
                                    onClick={() => { setRegisterStatusFilter('CHECKED_OUT'); fetchRegister(1, searchRegister, 'CHECKED_OUT', registerFromDate, registerToDate); }}>Checked Out</button>
                            </div>
                        </div>
                        
                        {loadingRegister ? (
                            <div className="loading-state">Loading register entries...</div>
                        ) : allRegister.length === 0 ? (
                            <div className="empty-state"><p>No register entries found</p></div>
                        ) : (
                            <>
                                <div className="cheques-table-container">
                                    <table className="cheques-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Check-In Time</th>
                                                <th>Check-Out Time</th>
                                                <th>Name</th>
                                                <th>Purpose</th>
                                                <th>Contact Number</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allRegister.map((entry) => (
                                                <tr key={entry._id}>
                                                    <td>{formatDate(entry.date)}</td>
                                                    <td>{formatTime(entry.checkInTime)}</td>
                                                    <td>{formatTime(entry.checkOutTime)}</td>
                                                    <td>{entry.name}</td>
                                                    <td className="purpose-cell">{entry.purpose}</td>
                                                    <td>{entry.contactNumber}</td>
                                                    <td>
                                                        <span className={`status-badge ${entry.status === 'CHECKED_IN' ? 'status-issued' : 'status-collected'}`}>
                                                            {entry.status === 'CHECKED_IN' ? 'Checked In' : 'Checked Out'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {totalRegisterPages > 1 && (
                                    <div className="pagination">
                                        <button onClick={() => fetchRegister(currentRegisterPage - 1, searchRegister, registerStatusFilter, registerFromDate, registerToDate)}
                                            disabled={currentRegisterPage === 1} className="pagination-btn">Previous</button>
                                        <span className="pagination-info">Page {currentRegisterPage} of {totalRegisterPages}</span>
                                        <button onClick={() => fetchRegister(currentRegisterPage + 1, searchRegister, registerStatusFilter, registerFromDate, registerToDate)}
                                            disabled={currentRegisterPage === totalRegisterPages} className="pagination-btn">Next</button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* TAB 6: DAILY DOCUMENTS */}
                {activeTab === 'monthly' && (
                    <div className="tab-content">
                        <div className="filters-card" style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                            <div className="filters-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                <div className="filter-group">
                                    <label>Business Group</label>
                                    <select value={selectedBusinessGroup} 
                                        onChange={(e) => setSelectedBusinessGroup(e.target.value)}
                                        className="form-select" style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}>
                                        <option value="">Select Business Group</option>
                                        {businessGroups.map(bg => (
                                            <option key={bg} value={bg}>{bg}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Branch</label>
                                    <select value={selectedBranch} 
                                        onChange={(e) => setSelectedBranch(e.target.value)}
                                        className="form-select" disabled={!selectedBusinessGroup}
                                        style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}>
                                        <option value="">Select Branch</option>
                                        {branches.map(branch => (
                                            <option key={branch} value={branch}>{branch}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Month</label>
                                    <select value={selectedMonth} 
                                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                        className="form-select" style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}>
                                        {months.map((month, index) => (
                                            <option key={index} value={index + 1}>{month}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Year</label>
                                    <select value={selectedYear} 
                                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                        className="form-select" style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}>
                                        {[2024, 2025, 2026, 2027, 2028].map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div style={{ marginTop: '1rem' }}>
                                <button className="btn btn-primary" onClick={fetchMonthlyDocument}
                                    disabled={loadingMonthly || !selectedBusinessGroup || !selectedBranch}
                                    style={{ padding: '0.5rem 1.5rem', background: '#711b45', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                                    {loadingMonthly ? 'Loading...' : '🔄 Refresh Document'}
                                </button>
                            </div>
                        </div>

                        {monthlyDoc && (
                            <div className="document-card" style={{ background: 'white', padding: '1.5rem', borderRadius: '12px' }}>
                                <div className="document-header" style={{ marginBottom: '1.5rem' }}>
                                    <h2>{monthlyDoc.businessGroup} - {monthlyDoc.branch}</h2>
                                    <h3>{months[monthlyDoc.month - 1]} {monthlyDoc.year}</h3>
                                </div>
                                <div className="reports-list">
                                    {monthlyDoc.reports.map((report, index) => (
                                        <div key={index} className="report-card" style={{ marginBottom: '2rem', padding: '1rem', background: '#f7fafc', borderRadius: '8px' }}>
                                            <h3 className="report-title" style={{ marginBottom: '1rem' }}>{report.reportName}</h3>
                                            {renderCalendar(report)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* View Cheque Modal */}
                {viewChequeModal && (
                    <div className="modal-overlay" onClick={() => setViewChequeModal(null)}>
                        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{viewChequeModal.chequeNumber ? 'Cheque Details' : 'Cash Payment Details'}</h2>
                                <button className="modal-close" onClick={() => setViewChequeModal(null)}>×</button>
                            </div>
                            <div className="modal-body">
                                <div className="cheque-details-section">
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>{viewChequeModal.chequeNumber ? 'Cheque Number:' : 'Voucher Number:'}</label>
                                            <span>{viewChequeModal.chequeNumber || viewChequeModal.voucherNumber}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Supplier:</label>
                                            <span>{viewChequeModal.supplierName}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Amount:</label>
                                            <span className="amount-highlight">{formatAmount(viewChequeModal.amount)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>{viewChequeModal.chequeNumber ? 'Cheque Date:' : 'Payment Date:'}</label>
                                            <span>{formatDate(viewChequeModal.chequeDate)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Status:</label>
                                            <span className={`status-badge status-${viewChequeModal.status}`}>
                                                {viewChequeModal.status === 'pending' && '🟡 Pending Issuance'}
                                                {viewChequeModal.status === 'collected' && '🟢 Issued to Supplier'}
                                                {viewChequeModal.status === 'returned' && '🔵 Returned'}
                                                {viewChequeModal.status === 'cancelled' && '🔴 Cancelled'}
                                            </span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Issued By:</label>
                                            <span>{viewChequeModal.issuedBy}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Issued Date:</label>
                                            <span>{formatDate(viewChequeModal.issuedAt)}</span>
                                        </div>
                                        {viewChequeModal.status === 'collected' && (
                                            <>
                                                <div className="detail-item">
                                                    <label>Receipt Number:</label>
                                                    <span>{viewChequeModal.receiptNumber}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Receiver Name:</label>
                                                    <span>{viewChequeModal.receiverName}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Contact Number:</label>
                                                    <span>{viewChequeModal.contactNumber}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Collected By:</label>
                                                    <span>{viewChequeModal.collectedBy}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Signatures Section */}
                                <div className="signatures-display-section">
                                    <h3>Signatures</h3>
                                    <div className="signatures-grid">
                                        {/* Accountant Signature */}
                                        <div className="signature-display">
                                            <label>Accountant Signature:</label>
                                            {viewChequeModal.accountantSignature ? (
                                                viewChequeModal.accountantSignature.startsWith('data:image') ? (
                                                    <img src={viewChequeModal.accountantSignature} alt="Accountant Signature" className="signature-image" />
                                                ) : (
                                                    <div className="signature-text-display">{viewChequeModal.accountantSignature}</div>
                                                )
                                            ) : (
                                                <div className="signature-placeholder">No signature available</div>
                                            )}
                                        </div>
                                        
                                        {/* Receiver Signature - Only if collected */}
                                        {viewChequeModal.status === 'collected' && (
                                            <div className="signature-display">
                                                <label>Receiver Signature:</label>
                                                {viewChequeModal.receiverSignature ? (
                                                    viewChequeModal.receiverSignature.startsWith('data:image') ? (
                                                        <img src={viewChequeModal.receiverSignature} alt="Receiver Signature" className="signature-image" />
                                                    ) : (
                                                        <div className="signature-text-display">{viewChequeModal.receiverSignature}</div>
                                                    )
                                                ) : (
                                                    <div className="signature-placeholder">No signature available</div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Receptionist Signature - Only if collected */}
                                        {viewChequeModal.status === 'collected' && (
                                            <div className="signature-display">
                                                <label>Receptionist Signature:</label>
                                                {viewChequeModal.receptionistSignature ? (
                                                    viewChequeModal.receptionistSignature.startsWith('data:image') ? (
                                                        <img src={viewChequeModal.receptionistSignature} alt="Receptionist Signature" className="signature-image" />
                                                    ) : (
                                                        <div className="signature-text-display">{viewChequeModal.receptionistSignature}</div>
                                                    )
                                                ) : (
                                                    <div className="signature-placeholder">No signature available</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setViewChequeModal(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManagerDashboard;
